import {
    type Column,
    type ColumnDef,
    type ColumnFiltersState,
    type FilterFnOption,
    type Row,
    type SortingFn,
    type SortingState,
    type Updater,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames/bind';
import { ReactNode, useCallback, useMemo } from 'react';

import { ChevronDown, ChevronUp } from '../../icons';
import Pagination from '../Pagination';
import { ColumnSelect, NumRowsPerPage, ResultsPerPageSelect, RowRange } from './components';
import DownloadAsCsv from './components/DownloadAsCsv/DownloadAsCsv';
import classes from './ConfigurableTable.module.scss';

const cx = classNames.bind(classes);

export interface ChildrenProps<T extends object = {}> {
    /** An array of column instances */
    allColumns: Column<T, unknown>[];
    /** The current column filter values. An array of objects, each having a column id and current filter value */
    currentColumnFilters: ColumnFiltersState;
    /** Any arbitrary value set as the global filter value */
    currentGlobalFilterValue: any;
    /** The array of rows right before filtering. Useful for building option lists in filters. */
    preFilteredRows: Row<T>[];
    /** An array of objects with column id and filter value. Pass an empty array to clear all column filters */
    setAllColumnFilters: (updater: Updater<ColumnFiltersState>) => void;
    /** Filter based upon the column ID and the data in specific columns */
    setColumnFilter: (columnId: string, newValue: any) => void;
    /** set any arbitrary value as the global filter value */
    setGlobalFilterValue: (filterValue: any) => void;
    /** Set or toggle wether all rows are expanded or not */
    toggleAllRowsExpanded: (value?: boolean) => void;
}

export interface ConfigurableTableProps<T extends object = {}> {
    /** A render prop for creating additional UI for the table e.g. filter menu */
    children?: (props: ChildrenProps<T>) => ReactNode;
    /** An array of `Column` objects - Must be memoized */
    columns: ColumnDef<T, any>[];
    /** TODO: Reimplement Add additional `filterTypes` for column or global filtering - Must be memoized */
    // customFilterTypes?: Record<
    //     string,
    //     (rows: Row<T>[], columnIds: IdType<T>[], filterValue: any) => Row<T>[]
    // >;
    /** Add additional `sortTypes` for column sorting - Must be memoized */
    customSortTypes?: Record<string, SortingFn<T>>;
    /** An array of data objects - Must be memoized */
    data: T[];
    /** An array of objects that specify the default filtered state by column - Must be memoized */
    defaultColumnFilters?: ColumnFiltersState;
    /** Global filter value can be any arbitrary value but be aware of what type of value your filterTypes will accept. Best used with a custom globalFilter - Must be memoized */
    defaultGlobalFilterValue?: any;
    /** An object of column id's as keys, and a boolean. string[] is to maintain backwards compatibility */
    defaultHiddenColumns?: Record<string, boolean> | string[];
    /** An array of objects that specify the column id and sort direction - Must be memoized */
    defaultSortBy?: SortingState;
    /** To enable debugTable in Tanstack Table */
    devOnlyDoNotUseInProductionDebugTable?: boolean;
    /** Can the table be downloaded in CSV format */
    downloadable?: boolean;
    /** Name to give downloaded csv file (don't supply file extension). Defaults to `key-download-<timestamp>` */
    downloadFilename?: string;
    /** Are rows allowed to expand to reveal nested rows */
    getRowCanExpand?: ((row: Row<T>) => boolean) | undefined;
    /** This optional function is used to access the sub rows for any given row */
    getSubRows?: (originalRow: T, index: number) => undefined | T[];
    /** Filter based upon your own arbitrary needs across any column in a given row - Must be memoized if a function */
    globalFilter?: FilterFnOption<T>;
    /** How many rows to show per page. This can be changed by the user. */
    rowsPerPage?: NumRowsPerPage;
    /** Array of column ID's that should stick when scrolling horizontally */
    stickyColumns?: string[];
}

export default function ConfigurableTable<T extends object = {}>({
    columns,
    data,
    defaultHiddenColumns,
    defaultSortBy = [],
    rowsPerPage = 20,
    // selectable,
    children,
    defaultColumnFilters = [],
    globalFilter = 'includesString', // Case-insensitive string inclusion
    // customFilterTypes,
    customSortTypes,
    defaultGlobalFilterValue,
    stickyColumns = [],
    getRowCanExpand,
    downloadable = false,
    downloadFilename,
    getSubRows,
    devOnlyDoNotUseInProductionDebugTable = false,
}: ConfigurableTableProps<T>) {
    const stuckColumns = useMemo(() => {
        const columns = [...stickyColumns];

        // if (selectable) {
        //     columns.push('selection');
        // }

        return columns;
    }, [stickyColumns]);

    const hiddenColumns = useMemo(() => {
        if (Array.isArray(defaultHiddenColumns)) {
            // If defaultHiddenColumns is an array, convert it to an object with boolean values to maintain backwards compatibility
            return defaultHiddenColumns.reduce((acc, columnId) => {
                acc[columnId] = false;

                return acc;
            }, {} as Record<string, boolean>);
        }

        return defaultHiddenColumns;
    }, [defaultHiddenColumns]);

    // TODO: Reimplement;
    // const sortTypes = useMemo<Record<string, SortByFn<T>>>(() => {
    //     return {
    //         /**
    //          * Overwrite the datetime sort function as it doesn't account for nullable
    //          * values. Fix below is taken from v8 lib.
    //          *
    //          * @src https://github.com/TanStack/table/pull/4110
    //          */
    //         datetime: (rowA, rowB, columnId) => {
    //             const a = rowA.values[columnId];
    //             const b = rowB.values[columnId];

    //             // Can handle nullish values
    //             // Use > and < because == (and ===) doesn't work with
    //             // Date objects (would require calling getTime()).
    //             // eslint-disable-next-line no-nested-ternary
    //             return a > b ? 1 : a < b ? -1 : 0;
    //         },
    //         ...customSortTypes,
    //     };
    // }, [customSortTypes]);

    const table = useReactTable(
        {
            columns,
            data,
            debugTable: devOnlyDoNotUseInProductionDebugTable,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
            getExpandedRowModel: getExpandedRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            getRowCanExpand,
            getSubRows,
            sortingFns: customSortTypes,
            initialState: {
                columnFilters: defaultColumnFilters,
                globalFilter: defaultGlobalFilterValue,
                pagination: {
                    pageSize: rowsPerPage,
                    pageIndex: 0,
                },
                columnPinning: {
                    left: stuckColumns,
                },
                columnVisibility: hiddenColumns,
                sorting: defaultSortBy,
            },
            // filterTypes: customFilterTypes,
            globalFilterFn: globalFilter,
        }
        // TODO: Reimplement
        // ({ visibleColumns }) => {
        //     if (selectable) {
        //         visibleColumns.push((columns) => [
        //             {
        //                 // Create the select column
        //                 id: 'selection',
        //                 width: 49,
        //                 header:({ getToggleAllRowsSelectedProps }) => {
        //                     const props = getToggleAllRowsSelectedProps();
        //                     const { checked, onChange, title } = props;

        //                     return (
        //                         <Checkbox
        //                             defaultChecked={checked}
        //                             hideLabel
        //                             label={title}
        //                             name="select-all"
        //                             onChange={onChange}
        //                             value=""
        //                         />
        //                     );
        //                 },
        //                 cell: (cell: CellContext<T>) => {
        //                     const { getToggleRowSelectedProps, id, isSelected } = cell.row;
        //                     const { onChange } = getToggleRowSelectedProps();

        //                     return (
        //                         <Checkbox
        //                             checked={isSelected}
        //                             controllable
        //                             hideLabel
        //                             label={`Toggle Row ${id} Selected`}
        //                             name="select-row"
        //                             onChange={(e) => {
        //                                 if (onChange) {
        //                                     onChange(e);
        //                                 }
        //                             }}
        //                             value=""
        //                         />
        //                     );
        //                 },
        //             },
        //             ...columns,
        //         ]);
        //     }
        // }
    );

    /**
     * To maintain backwards compatibility, we need to provide a way to set a column filter
     * by column ID and value. This was removed as a table method and moved to a column
     * method in v8.
     */
    const setColumnFilter = useCallback(
        (columnId: string, newValue: any) => {
            const columns = table.getAllColumns();
            const column = columns.find((col) => col.id === columnId);

            if (column && column.getCanFilter()) {
                column.setFilterValue(newValue);
            }
        },
        [table]
    );

    const pageSize = table.getState().pagination.pageSize;
    const pageCount = table.getPageCount();
    const rowCount = table.getRowCount();
    const currentPage = table.getState().pagination.pageIndex + 1;
    const shouldShowFooter = pageCount > 1 || downloadable;

    return (
        <>
            {children
                ? children({
                      preFilteredRows: table.getCoreRowModel().rows,
                      currentColumnFilters: table.getState().columnFilters,
                      allColumns: table.getAllColumns(),
                      setAllColumnFilters: table.setColumnFilters,
                      currentGlobalFilterValue: table.getState().globalFilter,
                      setGlobalFilterValue: table.setGlobalFilter,
                      toggleAllRowsExpanded: table.toggleAllRowsExpanded,
                      setColumnFilter,
                  })
                : null}
            <div className={classes.wrapper}>
                <div className={classes['scroll-container']}>
                    <table aria-rowcount={rowCount} className={cx(classes.table)}>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => {
                                return (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const { column } = header;
                                            const fixedWidth = column.columnDef.meta?.fixedWidth;
                                            const head = column.columnDef.header;
                                            const isSorted = header.column.getIsSorted();
                                            const sortDirection =
                                                isSorted !== false && isSorted === 'desc'
                                                    ? 'descending'
                                                    : 'ascending';

                                            const title =
                                                typeof head === 'string'
                                                    ? `Toggle ${head} SortBy`
                                                    : 'Toggle SortBy';

                                            const isPinned = column.getIsPinned();
                                            const isStickyColumn = isPinned !== false;
                                            // If a column is sticky or we specify it has a fixed width, we need to set a width. Default width is 150.
                                            const columnWidth =
                                                isStickyColumn || fixedWidth
                                                    ? column.getSize()
                                                    : undefined;

                                            return (
                                                <th
                                                    aria-sort={isSorted ? sortDirection : undefined}
                                                    className={cx({
                                                        sticky: isStickyColumn,
                                                    })}
                                                    key={header.id}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    style={{
                                                        left:
                                                            isPinned === 'left'
                                                                ? `${column.getStart('left')}px`
                                                                : undefined,
                                                        width: columnWidth,
                                                    }}
                                                    title={title}
                                                >
                                                    <div
                                                        className={cx('cell')}
                                                        style={{
                                                            width: columnWidth,
                                                        }}
                                                    >
                                                        <div className={cx('cell__content')}>
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                        </div>

                                                        {isSorted !== false ? (
                                                            <span
                                                                aria-hidden
                                                                className={
                                                                    classes['sort-indicator']
                                                                }
                                                            >
                                                                {isSorted === 'desc' ? (
                                                                    <ChevronDown
                                                                        height={7}
                                                                        width={14}
                                                                    />
                                                                ) : (
                                                                    <ChevronUp
                                                                        height={7}
                                                                        width={14}
                                                                    />
                                                                )}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell, index) => {
                                            const { column } = cell;
                                            const {
                                                columnDef: { meta },
                                            } = column;
                                            const wrap = meta?.wrap;
                                            const fixedWidth = meta?.fixedWidth;
                                            const isPinned = column.getIsPinned();
                                            const isStickyColumn = isPinned !== false;

                                            // If a column is sticky or we specify it has a fixed width, we need to set a width. Default width is 150.
                                            const columnWidth =
                                                isStickyColumn || fixedWidth
                                                    ? column.getSize()
                                                    : undefined;

                                            return (
                                                <td
                                                    className={cx({
                                                        sticky: isStickyColumn,
                                                    })}
                                                    key={cell.id}
                                                    style={{
                                                        left:
                                                            isPinned === 'left'
                                                                ? `${column.getStart('left')}px`
                                                                : undefined,
                                                        width: columnWidth,
                                                    }}
                                                >
                                                    <div
                                                        className={cx('cell')}
                                                        style={{
                                                            width: columnWidth,
                                                        }}
                                                    >
                                                        <div
                                                            className={cx('cell__content', {
                                                                'cell__content--wrap': wrap,
                                                            })}
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <ColumnSelect<T> options={table.getAllColumns()} />
            </div>
            {shouldShowFooter ? (
                <div className={classes.footer}>
                    <div className={classes.footer__details}>
                        {downloadable && rowCount >= 1 && (
                            <DownloadAsCsv
                                fileName={downloadFilename}
                                json={table.getRowModel().rows.map((row) => {
                                    /**
                                     * "rows" is a filtered and sorted array the same way the table is at the time of
                                     * clicking the "Download as CSV" button
                                     *
                                     * the returned value is an object representing the columns/values of the row. The
                                     * value of each column is what's returned from  the "accessor" attribute on the
                                     * columns array passed to the table.
                                     */
                                    return row.getVisibleCells().reduce((acc, cell) => {
                                        acc[cell.column.id] = cell.getValue();
                                        return acc;
                                    }, {} as Record<string, any>);
                                })}
                            />
                        )}

                        {pageCount > 1 ? (
                            <>
                                <ResultsPerPageSelect
                                    defaultValue={pageSize}
                                    onChange={(e) => {
                                        table.setPageSize(Number(e.target.value));
                                    }}
                                    totalNumRows={rowCount}
                                />

                                {/* <JumpToPage
                                    defaultValue={`${currentPage}`}
                                    onChange={(e) => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        gotoPage(page);
                                    }}
                                /> */}

                                <RowRange
                                    currentPage={currentPage}
                                    rowsPerPage={pageSize}
                                    totalRows={rowCount}
                                />
                            </>
                        ) : null}
                    </div>

                    {pageCount > 1 ? (
                        <Pagination
                            aria-label="Table Pagination"
                            currentPage={currentPage}
                            onGoTo={(pageNum) => {
                                table.setPageIndex(pageNum - 1);
                            }}
                            onNext={table.nextPage}
                            onPrev={table.previousPage}
                            totalNumPages={pageCount}
                        />
                    ) : null}
                </div>
            ) : null}
        </>
    );
}
